import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import { QRCodeSVG } from 'qrcode.react'
import { MdDownload, MdInfoOutline } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { IAppBuild } from '~/types/build'

import InstructionOne from '~/assets/build-instructions-1.png'
import InstructionTwo from '~/assets/build-instructions-2.png'
import InstructionThree from '~/assets/build-instructions-3.png'
import Badge from '~/components/Badge'
import Tooltip from '~/components/Tooltip'

import './styles.scss'

const instructionScreenshots = [InstructionOne, InstructionTwo, InstructionThree]

interface Props {
  build: IAppBuild
}

const PanelContent = ({ build }: Props): ReactElement => {
  /** Local state */

  const { created_at, direct_file_url, download_link, tags, version_name } = build

  /** Handlers */

  const handleRedirect = (url: string) => () => window.open(url, '_blank')

  /** Render */

  const steps: ReactElement[] = [
    <span key="instruction-1">
      In the <span className="bold">device settings</span>, in the{' '}
      <span className="bold">&quot;General&quot; tab, select</span> the &quot;
      <span className="bold">Device management&quot; tab.</span>
    </span>,
    <span key="instruction-2">
      Then <span className="bold">select the MWM editor.</span>
    </span>,
    <span key="instruction-3">
      Finally, <span className="bold">click on &quot;Trust MWM&quot;.</span>
    </span>,
  ]

  return (
    <div className="build-panel-content">
      <div className="build-panel-content__header flex flex-column">
        <div className="build-panel-content__header__version">
          Build {version_name}
          <MdDownload className="action-icon" color="#ff5301" onClick={handleRedirect(direct_file_url)} size={14} />
        </div>
        <div className="build-panel-content__header__information">
          <div className="build-panel-content__header__information-right">
            <div className="build-panel-content__header__information__tags">
              {tags?.map((tag) => <Badge className="build-badge" key={tag} value={tag} />)}
            </div>
            <div className="build-panel-content__header__information__date">
              {formatDate(created_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
            </div>
          </div>
        </div>
      </div>
      <div className="build-panel-content__body flex flex-column">
        <div className="build-panel-content__body__qr-code-container flex">
          <div className="build-panel-content__body__qr-code-container__qr-code">
            <QRCodeSVG level="Q" size={139} value={download_link} />
          </div>
          <div className="flex flex-column justify-center">
            <div className="build-panel-content__body__qr-code-container__scan">Scan to install the app</div>
            <div className="build-panel-content__body__qr-code-container__confidential">
              This version of the application is strictly <span className="bold">confidential</span>.
              <Tooltip
                align={{ points: ['br', 'tr'] }}
                className="build-panel__tooltip"
                overlay={
                  <div className="info-tooltip-overlay">
                    <div className="info-tooltip-overlay__section">
                      You may only <span className="bold">install it</span> on{' '}
                      <span className="bold">devices owned by MWM or MWM employees</span>.
                    </div>
                    <div className="info-tooltip-overlay__section">
                      You may not use this QRCode on your friends&apos; or family&apos;s devices.
                      <br />
                      Any <span className="bold">violation of this rule</span> may result in{' '}
                      <span className="bold">Apple removing our ability to use our applications internally</span>.
                    </div>
                    <div className="info-tooltip-overlay__section">
                      <span className="bold">We&apos;re counting on you!</span>
                    </div>
                  </div>
                }
                placement="top"
                trigger="hover"
              >
                <div>
                  <MdInfoOutline size={16} />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="build-panel-content__body__instruction">
          <div className="build-panel-content__body__instruction__title">Instructions</div>
          <div className="build-panel-content__body__instruction__description">
            After <span className="bold">the first installation</span> of an internal MWM app on a device, it is
            necessary to{' '}
            <span className="bold">tell the device that MWM apps installed outside of the AppStore are allowed</span>.
            Without this step, the apps will display a blocking pop-up at startup.
          </div>
          <div className="build-panel-content__body__instruction__step-container">
            {steps.map((step, idx) => (
              <div className="step-content-container" key={idx + 1}>
                <div className="step-content">
                  <div className="step-number">{idx + 1}</div>
                  <div className="step-instructions">{step}</div>
                </div>
                <div className="illu-container">
                  <img alt="IPhone general settings menu screenshot" src={instructionScreenshots[idx]} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PanelContent
